import {
  getRingCategory,
  getScheduledTiming,
  getTaskCategory,
  getTaskCategoryColor,
  getTaskInstructions,
  getTaskLoincCode,
  isAsNeeded,
} from '@liza/fhir-utils';
import { getLifestyleMeasurementDisplay } from '@liza/medplum-config/valueSet';
import type { WithId } from '@medplum/core';
import type { Observation, Task } from '@medplum/fhirtypes';
import { RectangleHorizontal, Rows } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCodeDisplay } from '../../hooks/useCodeDisplay';
import { useAutoAnimateRef } from '../../hooks/useAutoAnimateRef';
import type { TimingRepeat } from '../../utils/formatTiming';
import {
  expandComponentMeasurements,
  formatVitalValueParts,
  getMeasurementsTimeRange,
  groupObservations,
  LOINC_SYSTEM,
  toTrendSeries,
} from '../../utils/observation';
import { cn } from '../../utils/cn';
import { getScheduleLabel } from '../../utils/scheduleGrouping';
import { observationsThroughTime } from '../../utils/timeline';
import { ChartDurationToggle } from '../charts/ChartDurationToggle';
import { getChartDurationCutoff, type ChartDuration } from '../charts/chartDuration';
import { Button } from '../ui/button';
import { CarePlanSectionHeading } from './CarePlanSectionHeading';
import { CARE_PLAN_HEADING_GAP } from './carePlanRhythm';
import { useCarePlanTimelineScope } from './CarePlanTimelineScopeContext';
import { useTimelinePlayback } from './TimelinePlaybackContext';
import { ALL_SERIES_KEY } from './combinedChartKeys';
import { CombinedMeasurementChart, type CombinedSeries } from './CombinedMeasurementChart';
import { MeasurementDetailItem } from './MeasurementDetailItem';
import { MeasurementResultItem } from './MeasurementResultItem';
import { SuggestedActionWrapper } from './SuggestedActionWrapper';

// A warm chart palette anchored in purple and easing into pink and peach — cohesive with the brand
// but distinct enough to tell overlaid lines apart. Each series' tab dot uses its color as a key.
const CHART_PALETTE = ['#A78BFA', '#D08CF2', '#F472B6', '#FB7185', '#FBBF77'];

export type MeasurementsProps = {
  observations: WithId<Observation>[];
  measurementTasks?: WithId<Task>[];
  /** Result codes the CarePlan's Goals expect us to surface, with their display names.
   * Any code here without a matching observation renders an em-dash placeholder. */
  expectedMeasurements?: { code: string; name: string }[];
  /** When provided, each measurement group renders as a button that fires this on click,
   * passing the group's measurement Task so the edit dialog can open even when the task's
   * schedule portion is hidden. */
  onItemClick?: (task: WithId<Task>) => void;
  /** Render each row's chart large with labeled axes (used in the topic detail overlay). */
  detailed?: boolean;
  /**
   * Fired (detailed mode only) with the shared epoch-ms time span of these measurements, or null when
   * there is none / on unmount. Lets the host scope the structural timeline to the open topic. Must be
   * a stable reference (e.g. `useCallback`) to avoid effect thrash.
   */
  onTimeRangeChange?: (range: { start: number; end: number } | null) => void;
  /** Per-task suggested action attached beneath (and highlighting) the matching row, by editTask.id. */
  allSuggestionRowsByTaskId?: Map<string, React.ReactNode>;
  /**
   * Section heading label. Defaults to the localized "Results". Pass `null` to render the rows with
   * no heading — used when embedding lifestyle-measurement rows under the Lifestyle Changes heading.
   */
  heading?: string | null;
  /**
   * Topic-level synchronized hover group id. When provided, ties this section's charts into the same
   * crosshair group as the topic's other chart-bearing sections; otherwise a per-section id is used.
   */
  syncId?: string;
  /** Compact topic-card variant: forwarded to the rows so their instructions/schedule (and the
   * reading's unit) collapse until the card is hovered. Off by default so patient-web and the detail
   * overlay are unchanged. */
  compact?: boolean;
  /**
   * Render observation groups that have no measurement Task. Off by default because on a care plan a
   * task-less group is an orphaned Goal and must stay hidden; the labs view, which has no plan behind
   * it, is all task-less groups.
   */
  standalone?: boolean;
  /** Fired with a row's result code. In standalone mode this is what a row click does. */
  onResultClick?: (code: string) => void;
  /**
   * Row order within the section. `recency` (the default) puts the measurements with readings first,
   * newest first — what a care-plan topic wants, since a row without readings is the exception there.
   * `expected` keeps `expectedMeasurements`' own order instead, so a row holds its position whether or
   * not it has been measured: the labs view lists a whole panel, and a row that moved the first time
   * it was drawn would stop reading as a panel.
   */
  rowOrder?: 'recency' | 'expected';
  /**
   * Open the detailed view with the charts already combined onto one axis (when there are at least two
   * trends to combine). The stacked charts remain one toggle away. Off by default so the care-plan
   * overlay keeps opening stacked.
   */
  defaultCombined?: boolean;
  /**
   * Flag each row's latest value when the lab said it was abnormal or it falls outside its reference
   * range. Forwarded to the rows; off by default so the care plan, whose readings carry no range, is
   * unchanged. The labs view turns it on so expanding a category keeps the flag its collapsed line shows.
   */
  flagAbnormal?: boolean;
};

/**
 * Whether a measurement Task should render its visible schedule portion. A task with no cadence —
 * one-time, "None", or a bare "As needed" — produces no meaningful schedule line, so we hide the row
 * entirely; the group remains clickable so the task can still be edited. A PRN cadence does have one
 * ("Weekly as needed"), so it keeps its row.
 */
function hasVisibleSchedule(task: WithId<Task>): boolean {
  const timing = getScheduledTiming(task) as TimingRepeat | null;
  if (!timing) return false;
  return getScheduleLabel(timing, undefined, isAsNeeded(task)) !== undefined;
}

/** Section heading row + the detail-overlay "combine/stack charts" toggle. Returns nothing when
 * there's no heading and no toggle (e.g. lifestyle-measurement rows embedded with `heading={null}`).
 * A render helper (not a component) so its branches don't count toward `Measurements`' complexity. */
function renderMeasurementsHeader({
  headingLabel,
  canCombine,
  combined,
  onToggleCombined,
  stackLabel,
  combineLabel,
  durationToggle,
}: {
  headingLabel: string | null;
  canCombine: boolean;
  combined: boolean;
  onToggleCombined: () => void;
  stackLabel: string;
  combineLabel: string;
  durationToggle?: React.ReactNode;
}): React.ReactNode {
  if (headingLabel === null && !canCombine && !durationToggle) return null;
  // The row only reserves a control's height when it has one — both controls are `detailed`-only, so on
  // a topic card this is a heading and nothing else, and reserving 28px for it sat the heading 4px lower
  // than every other section's.
  const hasControls = canCombine || Boolean(durationToggle);
  return (
    <div className={cn('flex items-center justify-between gap-2 px-1', hasControls && 'min-h-7')}>
      {headingLabel !== null && <CarePlanSectionHeading section="results">{headingLabel}</CarePlanSectionHeading>}
      <div className="ml-auto flex items-center gap-1">
        {canCombine && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onToggleCombined}
            className="h-7 px-2 text-xs text-muted-foreground"
          >
            {combined ? <Rows /> : <RectangleHorizontal />}
            {combined ? stackLabel : combineLabel}
          </Button>
        )}
        {durationToggle}
      </div>
    </div>
  );
}

/**
 * "Results" section rendered on each CarePlan topic card. Groups Observations,
 * measurement Task templates, and Goal-expected LOINCs by LOINC code. Each code renders as a
 * single `MeasurementResultItem` row — name + schedule on the left, trend sparkline in the
 * middle, latest value + timestamp on the right (an em-dash when a Goal expects the measurement
 * but no Observation exists yet). Returns null when nothing is renderable.
 */
export function Measurements({
  observations,
  measurementTasks,
  expectedMeasurements,
  onItemClick,
  detailed,
  onTimeRangeChange,
  allSuggestionRowsByTaskId,
  heading,
  syncId: syncIdProp,
  compact,
  standalone,
  onResultClick,
  rowOrder = 'recency',
  defaultCombined = false,
  flagAbnormal,
}: MeasurementsProps): React.ReactElement | null {
  const { t } = useTranslation('records');
  const { displayFor, unitFor, lang } = useCodeDisplay();
  // Scrubbed instant (null = live, or flag off): charts reveal only data up to this time. `zoomRange`
  // mirrors the timeline's zoom window in the detail overlay.
  const { currentTime, zoomRange, requestZoom } = useTimelinePlayback();
  // The topic's canonical shared domain (union of every chart's span), when a detail overlay scopes it.
  const { topicRange } = useCarePlanTimelineScope();
  const headingLabel = heading === undefined ? t('results') : heading;

  // Snap observations onto the LOINCs the Goals expect, so panel variants that list the same code among
  // multiple codings (e.g. Epic-imported BP) group with app-generated readings.
  const expectedLoincs = useMemo(
    () => (expectedMeasurements?.length ? new Set(expectedMeasurements.map((m) => m.code)) : undefined),
    [expectedMeasurements],
  );

  // Every measurement code this view lists — expected (Goals) plus edit-task templates. Sleep stage
  // durations arrive as components of one panel Observation; surfacing each as its own measurement
  // (gated to these codes so BP stays intact) shows a value instead of an em-dash.
  const measurementCodes = useMemo(() => {
    const codes = new Set<string>(expectedLoincs);
    for (const task of measurementTasks ?? []) {
      const loinc = getTaskLoincCode(task);
      if (loinc) codes.add(loinc);
    }
    return codes;
  }, [expectedLoincs, measurementTasks]);

  // Stable display order for observation-bearing measurements: their positions in the FULL (component-
  // expanded) dataset, so rows keep fixed positions while scrubbing instead of jumping around as each
  // reading crosses the playhead (the sort is by latest reading, which changes as data reveals). Live
  // order is unchanged.
  const orderedIdentifiers = useMemo(
    () =>
      groupObservations(expandComponentMeasurements(observations, measurementCodes), expectedLoincs).map(
        (group) => group.identifier,
      ),
    [observations, measurementCodes, expectedLoincs],
  );

  // Insertion order matters: observation-bearing LOINCs render first (in the stable order above), then
  // task-only / goal-only LOINCs in encounter order.
  const groups = useMemo(() => {
    type Group = {
      observationGroup?: ReturnType<typeof groupObservations>[number];
      tasks: WithId<Task>[];
      placeholderName?: string;
    };
    const byLoinc = new Map<string, Group>();

    // Reveal charts as the timeline is scrubbed: group observations at or before the instant (null =
    // live → all), expanding component measurements after the time filter (a synthetic stage inherits
    // its panel's timestamp, so filtering first keeps the steps independent). Insert them in the stable
    // full-data order so revealed rows keep fixed positions as the playhead advances; the axis
    // (sharedDomain) still spans the full range so it never rescales.
    const expandedObservations = expandComponentMeasurements(
      observationsThroughTime(observations, currentTime),
      measurementCodes,
    );
    const revealedGroups = new Map(
      groupObservations(expandedObservations, expectedLoincs).map((group) => [group.identifier, group] as const),
    );
    for (const identifier of orderedIdentifiers) {
      const observationGroup = revealedGroups.get(identifier);
      if (observationGroup) byLoinc.set(identifier, { observationGroup, tasks: [] });
    }

    for (const task of measurementTasks ?? []) {
      const loinc = getTaskLoincCode(task);
      if (!loinc) continue;
      const group = byLoinc.get(loinc) ?? { tasks: [] };
      group.tasks.push(task);
      byLoinc.set(loinc, group);
    }

    for (const { code, name } of expectedMeasurements ?? []) {
      const existing = byLoinc.get(code);
      if (!existing) {
        byLoinc.set(code, { tasks: [], placeholderName: name });
      } else if (existing.observationGroup) {
        // Use the Goal's display name so the row label is stable across observation sources.
        existing.observationGroup = { ...existing.observationGroup, name };
      } else {
        existing.placeholderName = name;
      }
    }

    if (rowOrder !== 'expected') return byLoinc;
    // Re-key in the order the host listed, so an unmeasured row keeps its slot. Anything the host did
    // not list (a reading on a code outside the expected set) follows, in the order it grouped.
    const ordered = new Map<string, Group>();
    for (const { code } of expectedMeasurements ?? []) {
      const group = byLoinc.get(code);
      if (group) ordered.set(code, group);
    }
    for (const [code, group] of byLoinc) if (!ordered.has(code)) ordered.set(code, group);
    return ordered;
  }, [
    observations,
    measurementTasks,
    expectedMeasurements,
    currentTime,
    expectedLoincs,
    measurementCodes,
    orderedIdentifiers,
    rowOrder,
  ]);

  // One shared time domain across every measurement in this topic, so the stacked detail charts
  // align on a single axis instead of each auto-scaling to its own range. Only the detail overlay
  // renders charts, so the compact column skips this.
  const sharedDomain = useMemo<[number, number] | undefined>(() => {
    if (!detailed) return undefined;
    const range = getMeasurementsTimeRange(observations);
    return range ? [range.start, range.end] : undefined;
  }, [detailed, observations]);

  // A zoomable timeline (the provider detail overlay) already owns the charts' window; the duration
  // toggle is the timescale control for hosts without one (the patient-facing overlay).
  const [chartDuration, setChartDuration] = useState<ChartDuration>('all');
  const showDurationToggle = Boolean(detailed) && !requestZoom;
  const durationCutoffMs = useMemo(
    () => (showDurationToggle ? getChartDurationCutoff(chartDuration)?.getTime() : undefined),
    [showDurationToggle, chartDuration],
  );

  // What the charts actually render on: the timeline's zoom window when zoomed, else the selected
  // duration window (a rolling window ending now), else the topic's canonical shared domain (union
  // across every chart-bearing section), else this section's own observation span as a local
  // fallback. Kept separate from `sharedDomain` so the published time-range (below) still reports
  // the measurements' own span, and the axis doesn't rescale as points reveal while scrubbing. The
  // union only ever widens, so charts paint on their own extent then widen outward — no data is
  // excluded.
  const chartDomain = useMemo<[number, number] | undefined>(() => {
    if (!detailed) return sharedDomain;
    if (zoomRange) return [zoomRange.start.getTime(), zoomRange.end.getTime()];
    if (durationCutoffMs !== undefined) return [durationCutoffMs, Date.now()];
    if (topicRange) return [topicRange.start.getTime(), topicRange.end.getTime()];
    return sharedDomain;
  }, [detailed, zoomRange, durationCutoffMs, topicRange, sharedDomain]);

  // A per-Results id ties this topic's charts into one synchronized hover group (shared crosshair).
  // The host may override it with a topic-level id so every section shares one crosshair.
  const localSyncId = useId();
  const syncId = syncIdProp ?? localSyncId;
  const measurementListRef = useAutoAnimateRef<HTMLDivElement>();

  // Separate per-measurement charts are the default (each aligned on the shared time axis); "Combine
  // charts" overlays them all on one normalized axis.
  const [combined, setCombined] = useState(defaultCombined);
  // The focused series in the combined view, owned here so the matching task rows render below the
  // chart. Defaults to "All" — every line lit, every task listed.
  const [activeKey, setActiveKey] = useState<string>(ALL_SERIES_KEY);

  // The measurement groups we can actually render: each needs an edit Task template and a resolvable
  // name. A group with no task is an orphaned active Goal (its Task was cancelled) or a bare reading —
  // visible but uneditable, so we drop it. Prefer the curated lab label, then the Goal/observation
  // display name (stable across sources), then the placeholder.
  const renderable = useMemo(
    () =>
      Array.from(groups, ([loinc, { observationGroup, tasks, placeholderName }]) => {
        const editTask = tasks[0];
        if (!editTask && !standalone) return null;
        // Named by the code the row is keyed on — the plan's own code — rather than by whichever
        // reading is newest, so a row holding several variants of one measurement keeps one label.
        // Prefer a curated/translated label, then the lifestyle catalog's localized name (so a
        // no-reading lifestyle row still localizes rather than falling to the English Goal name), then
        // the observation's own display, then the Task/Goal-provided name, then the placeholder — but
        // never let `displayFor`'s raw-LOINC-code fallback shadow any of those.
        // `getLifestyleMeasurementDisplay` returns undefined for clinical codes, so their placeholder
        // name is unaffected.
        const name =
          displayFor({ coding: [{ system: LOINC_SYSTEM, code: loinc }] }, { fallbackToCode: false }) ||
          getLifestyleMeasurementDisplay(loinc, lang) ||
          displayFor(observationGroup?.latest.code, { fallbackToCode: false }) ||
          observationGroup?.name ||
          placeholderName;
        if (!name) return null;
        const timing = editTask ? (getScheduledTiming(editTask) as TimingRepeat | null) : null;
        const scheduleLabel =
          editTask && hasVisibleSchedule(editTask)
            ? getScheduleLabel(timing, undefined, isAsNeeded(editTask))
            : undefined;
        const instructions = (editTask && getTaskInstructions(editTask)) || undefined;
        return {
          loinc,
          name,
          editTask,
          observationGroup,
          scheduleLabel,
          instructions,
          // Localized unit for a lifestyle measurement; undefined for clinical measurements, whose
          // recorded units come straight from the Observation.
          unitOverride: unitFor(loinc),
          // The row's icon/color follows the task category — which for a lifestyle measurement is its
          // lifestyle domain (movement/food/sleep/mind), and for a clinical measurement is `measurement`.
          // A task-less standalone row is a plain measurement.
          category: editTask ? getTaskCategory(editTask) : ('measurement' as const),
        };
      }).filter((m): m is NonNullable<typeof m> => m !== null),
    [groups, displayFor, unitFor, lang, standalone],
  );

  // Series for the combined overlay: only measurements with a drawable trend (≥2 points), each given a
  // distinct palette color so the overlaid lines are tellable apart.
  const chartSeries = useMemo<CombinedSeries[]>(() => {
    if (!detailed) return [];
    const series: CombinedSeries[] = [];
    for (const m of renderable) {
      const results = m.observationGroup?.results;
      const latest = m.observationGroup?.latest;
      if (!results || !latest || results.length < 2) continue;
      const { data } = toTrendSeries(results);
      if (data.length < 2) continue;
      series.push({
        key: m.loinc,
        label: m.name,
        color: CHART_PALETTE[series.length % CHART_PALETTE.length]!,
        unit: m.unitOverride ?? formatVitalValueParts(latest).unit,
        data,
      });
    }
    return series;
  }, [detailed, renderable]);

  // Publish the shared span so the host can scope the structural timeline to the open topic; clear on
  // unmount (overlay close) so the timeline reverts to its full range. Keyed on the primitive
  // start/end (not the `sharedDomain` array) so it only fires when the window actually changes — the
  // array gets a fresh identity each render (its source `observations` does), which would otherwise
  // re-fire the effect on every render and loop with the host's setState.
  const domainStart = sharedDomain?.[0];
  const domainEnd = sharedDomain?.[1];
  useEffect(() => {
    if (!detailed || !onTimeRangeChange) return;
    onTimeRangeChange(
      domainStart !== undefined && domainEnd !== undefined ? { start: domainStart, end: domainEnd } : null,
    );
    return (): void => onTimeRangeChange(null);
  }, [detailed, onTimeRangeChange, domainStart, domainEnd]);

  if (groups.size === 0 || renderable.length === 0) return null;

  // Match the combined chart's palette so a measurement keeps the same color across both views.
  const colorByLoinc = new Map(chartSeries.map((s) => [s.key, s.color]));

  // What clicking a row does: edit its Task where there is one and the host takes edits, else report
  // the result code (the labs view's "open this lab"), else nothing and the row is not a button.
  const rowAction = (m: (typeof renderable)[number]): (() => void) | undefined => {
    if (m.editTask && onItemClick) {
      const task = m.editTask;
      return (): void => onItemClick(task);
    }
    if (onResultClick) return (): void => onResultClick(m.loinc);
    return undefined;
  };
  // A detailed stack can share a page with the plain list of the same rows (a detail column beside
  // its list), so detailed rows carry a distinct id.
  const rowTestId = (m: (typeof renderable)[number]): string =>
    `care-plan-measurement-${detailed ? 'detail-' : ''}row-${m.editTask?.id ?? m.loinc}`;

  // In the detail overlay only the header is the edit target, so the large chart below stays
  // interactive (its own tooltip fires on hover/click instead of opening the edit dialog).
  const rows = renderable.map((m) => {
    const editTestId = rowTestId(m);
    if (detailed) {
      // Lifestyle measurements chart in their green category color; clinical measurements keep the
      // palette color shared with the combined view.
      const isLifestyle = m.category ? getRingCategory(m.category) === 'lifestyle' : false;
      return (
        <div key={m.loinc} className="flex flex-col">
          <MeasurementDetailItem
            name={m.name}
            results={m.observationGroup?.results}
            latest={m.observationGroup?.latest}
            scheduleLabel={m.scheduleLabel}
            instructions={m.instructions}
            category={m.category}
            unitOverride={m.unitOverride}
            domain={chartDomain}
            syncId={syncId}
            color={isLifestyle ? getTaskCategoryColor(m.category) : colorByLoinc.get(m.loinc)}
            onEdit={rowAction(m)}
            editTestId={editTestId}
            trackedCode={m.loinc}
            flagAbnormal={flagAbnormal}
          />
        </div>
      );
    }

    // Suggestions are suppressed in the detail overlay (the `detailed` branch returned above), so this
    // only applies to the compact column.
    const suggestedAction = m.editTask ? allSuggestionRowsByTaskId?.get(m.editTask.id) : undefined;
    const content = (
      <MeasurementResultItem
        name={m.name}
        results={m.observationGroup?.results}
        latest={m.observationGroup?.latest}
        scheduleLabel={m.scheduleLabel}
        instructions={m.instructions}
        category={m.category}
        unitOverride={m.unitOverride}
        compact={compact}
        flagAbnormal={flagAbnormal}
      />
    );

    // The whole compact row is the click target so it stays editable even when no schedule row is
    // shown (e.g. "As needed"). Falls back to a plain group in read-only mode (no onItemClick).
    const onClick = rowAction(m);
    if (onClick) {
      return (
        <SuggestedActionWrapper key={m.loinc} suggestedAction={suggestedAction}>
          <Button
            type="button"
            variant="ghost"
            onClick={onClick}
            className="flex h-auto w-full flex-col items-stretch gap-0 p-0 text-left font-normal whitespace-normal hover:bg-accent/50"
            aria-label={m.name}
            data-testid={editTestId}
          >
            {content}
          </Button>
        </SuggestedActionWrapper>
      );
    }

    return (
      <SuggestedActionWrapper key={m.loinc} suggestedAction={suggestedAction}>
        <div className="flex flex-col">{content}</div>
      </SuggestedActionWrapper>
    );
  });

  // Combining one series is pointless — only offer the toggle when there's more than one trend.
  const canCombine = Boolean(detailed) && chartSeries.length >= 2;
  const activeChartKey =
    activeKey === ALL_SERIES_KEY || chartSeries.some((s) => s.key === activeKey) ? activeKey : ALL_SERIES_KEY;
  // "All" lists every measurement's row below the chart; a single tab lists just that one.
  const detailRows =
    activeChartKey === ALL_SERIES_KEY ? renderable : renderable.filter((m) => m.loinc === activeChartKey);

  return (
    <div className={cn('flex flex-col', detailed ? 'gap-3' : CARE_PLAN_HEADING_GAP)}>
      {renderMeasurementsHeader({
        headingLabel,
        canCombine,
        combined,
        onToggleCombined: () => setCombined((prev) => !prev),
        stackLabel: t('stackCharts'),
        combineLabel: t('combineCharts'),
        durationToggle: showDurationToggle ? (
          <ChartDurationToggle value={chartDuration} onChange={setChartDuration} />
        ) : undefined,
      })}
      {canCombine && combined && chartDomain ? (
        <div className="flex flex-col gap-2">
          <CombinedMeasurementChart
            series={chartSeries}
            domain={chartDomain}
            syncId={syncId}
            activeKey={activeChartKey}
            onActiveKeyChange={setActiveKey}
            allLabel={t('all')}
          />
          <div ref={measurementListRef} className="flex flex-col gap-3">
            {detailRows.map((m) => (
              <MeasurementDetailItem
                key={m.loinc}
                name={m.name}
                results={m.observationGroup?.results}
                latest={m.observationGroup?.latest}
                scheduleLabel={m.scheduleLabel}
                instructions={m.instructions}
                category={m.category}
                unitOverride={m.unitOverride}
                showChart={false}
                onEdit={rowAction(m)}
                editTestId={rowTestId(m)}
                flagAbnormal={flagAbnormal}
              />
            ))}
          </div>
        </div>
      ) : detailed ? (
        // Always a single vertical stack so the charts share the full width and line up on time.
        <div ref={measurementListRef} className="flex flex-col gap-3">
          {rows}
        </div>
      ) : (
        <div ref={measurementListRef} className="flex flex-col">
          {rows}
        </div>
      )}
    </div>
  );
}
